"use client";

import Link from "next/link";
import { ArrowRight, BookOpen } from "lucide-react";
import MaxWidthWrapper from "@/components/shared/max-width-wrapper";
import { Icons } from "@/components/shared/icons";
import { siteConfig } from "@/config/site";
import { useIsLocalhost } from "@/hooks/use-is-localhost";
import { cn } from "@/lib/utils";

function MastercardIcon({ className }: { className?: string }) {
  return (
    <svg className={cn("h-4 w-7", className)} viewBox="0 0 36 24" fill="currentColor">
      <circle cx="13" cy="12" r="9" fill="currentColor" fillOpacity="0.7" />
      <circle cx="23" cy="12" r="9" fill="currentColor" fillOpacity="0.4" />
    </svg>
  );
}

function ApplePayIcon({ className }: { className?: string }) {
  return (
    <div className={cn("flex items-center gap-0.5 text-xs font-semibold tracking-tight", className)}>
      <svg className="size-3 fill-current" viewBox="0 0 170 170">
        <path d="M150.37 130.25c-2.45 5.66-5.35 10.87-8.71 15.66-4.58 6.53-8.33 11.05-11.22 13.56-4.48 4.12-9.28 6.23-14.42 6.35-3.69 0-8.14-1.05-13.32-3.18-5.19-2.12-9.97-3.17-14.34-3.17-4.58 0-9.49 1.05-14.75 3.17-5.26 2.13-9.5 3.24-12.74 3.35-4.35.13-9.16-1.9-14.42-6.08-3.7-3.04-7.6-7.85-11.71-14.42-5.46-8.7-9.71-18.42-12.75-29.17-3.04-10.74-4.56-21.11-4.56-31.11 0-14.35 3.65-26.04 10.95-35.07 7.3-9.03 16.5-13.62 27.6-13.78 5.76 0 11.71 1.49 17.86 4.47 6.14 2.99 10.15 4.54 12.02 4.65 1.54 0 5.86-1.68 12.98-5.04 7.12-3.36 13.53-4.79 19.24-4.29 14.89 1.19 26.2 6.94 33.93 17.26-13.26 8.04-19.78 19.29-19.55 33.74.22 11.3 4.53 20.82 12.92 28.56 3.8 3.59 8.16 6.36 13.08 8.3-2.6 7.6-5.87 15.22-9.8 22.88zM119.22 33.36c-.11-7.28 2.45-14.28 7.68-21.01 5.23-6.73 11.83-11.45 19.8-14.16.22 2.61.16 5.34-.18 8.19-.65 5.54-3.05 11.41-7.2 17.61-4.14 6.2-9.67 10.23-16.58 12.08-.87-1.19-2.04-2.1-3.52-2.71z" />
      </svg>
      Pay
    </div>
  );
}

export default function CTABanner() {
  const isLocalhost = useIsLocalhost();

  return (
    <section className="pb-12 bg-background">
      <MaxWidthWrapper>
        <div className="relative overflow-hidden rounded-2xl sm:rounded-3xl border border-border/80 bg-card pt-8 p-6 shadow-xs transition-colors">
          {/* Subtle Ambient Radial Glow */}
          <div
            aria-hidden="true"
            className="pointer-events-none absolute -right-20 -top-20 size-[450px] rounded-full bg-[radial-gradient(circle_at_70%_30%,rgba(99,102,241,0.12),transparent_65%)] blur-3xl dark:bg-[radial-gradient(circle_at_70%_30%,rgba(129,140,248,0.18),transparent_65%)]"
          />

          {/* Top Main Section */}
          <div className="relative flex flex-col justify-between gap-6 lg:flex-row lg:items-center lg:gap-8">
            {/* Left Content */}
            <div className="flex max-w-xl flex-col items-start gap-2.5">
              <span className="font-mono text-xs font-bold uppercase tracking-wider text-indigo-600 dark:text-indigo-400">
                Level Up Your Gameplay
              </span>

              <h2 className="font-urban text-2xl font-extrabold tracking-tight text-foreground sm:text-3xl md:text-4xl lg:text-5xl">
                Ready to <span className="bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 bg-clip-text text-transparent">Dominate?</span>
              </h2>

              <p className="text-sm text-muted-foreground sm:text-base leading-relaxed">
                Join thousands of satisfied customers and experience the difference today.
              </p>
            </div>

            {/* Right Buttons */}
            <div className="flex flex-wrap items-center gap-3 sm:gap-4 shrink-0">
              <a
                href="#products"
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-3.5 text-sm font-bold text-white shadow-lg shadow-indigo-500/25 transition-all duration-200 hover:from-indigo-500 hover:to-purple-500 hover:shadow-indigo-500/35 active:scale-[0.98]"
              >
                <span>Browse Store</span>
                <ArrowRight className="size-4" />
              </a>

              {isLocalhost ? (
                <Link
                  href="/docs"
                  className="inline-flex items-center justify-center gap-2 rounded-xl border border-border bg-muted/50 dark:bg-muted/30 px-6 py-3.5 text-sm font-semibold text-foreground transition-all duration-200 hover:bg-muted/80 dark:hover:bg-muted/60 hover:border-border active:scale-[0.98] shadow-2xs"
                >
                  <BookOpen className="size-4 text-muted-foreground" />
                  <span>View Guides</span>
                </Link>
              ) : (
                siteConfig.links.discord && (
                  <a
                    href={siteConfig.links.discord}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-2 rounded-xl border border-[#5865F2]/40 bg-[#5865F2]/10 hover:bg-[#5865F2]/20 dark:bg-[#5865F2]/15 dark:hover:bg-[#5865F2]/25 px-6 py-3.5 text-sm font-semibold text-foreground transition-all duration-200 hover:border-[#5865F2]/60 active:scale-[0.98] shadow-2xs"
                  >
                    <Icons.discord className="size-4 fill-current text-[#5865F2]" />
                    <span>Join Discord</span>
                  </a>
                )
              )}
            </div>
          </div>

          {/* Bottom Payment Methods */}
          <div className="relative mt-8 flex flex-col gap-4 border-t border-border/70 pt-6 sm:flex-row sm:items-center sm:justify-between">
            <span className="font-mono text-[11px] font-semibold uppercase tracking-widest text-muted-foreground/70">
              Secure Payments Via
            </span>

            <div className="flex flex-wrap items-center gap-5 text-muted-foreground/70">
              <span className="font-bold italic tracking-tighter text-xs">VISA</span>
              <MastercardIcon />
              <div className="text-xs font-medium tracking-tight">
                <span className="font-bold">G</span> Pay
              </div>
              <ApplePayIcon />
              <div className="flex size-5 items-center justify-center rounded-full bg-foreground/10 text-[11px] font-bold text-muted-foreground">
                ₿
              </div>
              <div className="flex size-5 items-center justify-center rounded-full bg-foreground/10 text-[11px] font-bold text-muted-foreground">
                Ł
              </div>
            </div>
          </div>
        </div>
      </MaxWidthWrapper>
    </section>
  );
}

